import { adaNotificationSelectors } from 'ducks/adaNotification'
import { authSelectors } from 'ducks/auth'
import { rolesSelectors } from 'ducks/auth_roles'
import { permissionsSelectors } from 'ducks/permissions'
import { useHasBetaFeatures } from 'layout/widgets/FeatureSettingsButton'
import { AvatarIcon, Badge, BadgeProps, Button, ButtonProps, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { forwardRef, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { OpenSolarTheme } from 'Themes'
import { OrgType } from 'types/orgs'
import { getRoleIdFromState } from 'util/misc'
const ACCOUNT_IMAGE_SIZE = 20

const UserImg = styled('img')({
  borderRadius: '50%',
  height: ACCOUNT_IMAGE_SIZE,
  width: ACCOUNT_IMAGE_SIZE,
})
const AccountButton = styled(Button)(() => ({
  minWidth: 0,
  gap: 12,
  '& img': {
    height: ACCOUNT_IMAGE_SIZE,
  },
  '& .OSUI-Button-endIcon': {
    margin: 0,
  },
}))

interface NavigationAccountButtonProps extends ButtonProps {
  logoPublicUrl: OrgType['logo_public_url']
}

export const MissingRole = () => {
  const translate = useTranslate()
  const roleId = useSelector(getRoleIdFromState)

  if (!roleId) {
    return (
      <div>
        <Typography textVariant="caption1">{translate('Please contact your administrator for access.')}</Typography>
      </div>
    )
  }
  return (
    <div>
      <Typography textVariant="caption1" colorHex={OpenSolarTheme.greyDark2 as `#${string}`}>
        {translate('Complete your profile details to help your team collaborate better.')}
      </Typography>
      <Typography textVariant="caption1" colorHex={OpenSolarTheme.blue4 as `#${string}`}>
        <u>{translate('Complete my Details')}</u>
      </Typography>
    </div>
  )
}

const NavigationAccountButton = forwardRef<HTMLButtonElement, NavigationAccountButtonProps>(
  ({ logoPublicUrl, ...buttonProps }, ref) => {
    const [hasOrgImageError, setHasOrgImageError] = useState(false)
    const [hasUserImageError, setHasUserImageError] = useState(false)
    const currentRole = useSelector(authSelectors.getCurrentRole)

    const roleComplete: boolean = useSelector(rolesSelectors.getIsRoleComplete)
    const { allowView } = useSelector((state) => permissionsSelectors.getPermissionByKey(state, 'team'))
    const { hasAnyFeatures } = useHasBetaFeatures()
    const isAdmin = useSelector(authSelectors.getIsAdmin)
    const hasAdaPendingReview = useSelector(adaNotificationSelectors.getHasPendingReview) && isAdmin

    const warningDotData:
      | {
          color: BadgeProps['color']
          id: string
        }
      | undefined = useMemo(() => {
      if (hasAdaPendingReview) {
        // Show info dot for Ada pending review (highest priority - user action needed)
        return { color: 'info', id: 'ada-pending-review-badge' }
      } else if (!roleComplete && allowView) {
        // Show red error dot if role incomplete
        return { color: 'error', id: 'role-incomplete-badge' }
      } else if (hasAnyFeatures) {
        // else, show blue 'Beta Features' dot, if any beta features are available
        // See: https://github.com/open-solar/opensolar-todo/issues/20284
        return { color: 'info', id: 'beta-features-badge' }
      }
    }, [hasAdaPendingReview, roleComplete, allowView, hasAnyFeatures])

    return (
      <AccountButton
        color="default"
        id="account-button"
        endIcon={
          currentRole && !hasUserImageError ? (
            <UserImg
              alt="nav-user-logo"
              onError={() => setHasUserImageError(true)}
              src={currentRole.portrait_image_public_url || ''}
            />
          ) : (
            <AvatarIcon height="20" width="20" />
          )
        }
        ref={ref}
        variant="outlined"
        {...buttonProps}
      >
        {logoPublicUrl && !hasOrgImageError && (
          <img alt="nav-org-logo" onError={() => setHasOrgImageError(true)} src={logoPublicUrl.toString()} />
        )}
        {warningDotData && <Badge id={warningDotData.id} variant="dot" color={warningDotData.color} />}
      </AccountButton>
    )
  }
)

export default NavigationAccountButton
